import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { Table } from '@interstate/components/Table';
import { Popover } from '@interstate/components/Popover';
import { CheckBox } from '@interstate/components/CheckBox';
import { Action } from '@interstate/components/Action';
import { InformationCircleIcon } from '@interstate/components/Icons/InformationCircleIcon';

import { nestedColumnsCompactUI } from './programColumns';

export type IncentivesTableViewRow = {
    incentiveId: number;
    programName: string;
    description: string;
    selected: boolean;
    [key: string]: any;
};

export type IncentiveDisabledReason = { kind: string; message: string };

export type IncentivesTableViewProps<Row extends IncentivesTableViewRow> = {
    rows: Row[];
    getDisabledReason: (row: Row) => IncentiveDisabledReason | undefined;
    isCheckboxDisabled: (row: Row, disabledReason: IncentiveDisabledReason | undefined) => boolean;
    onToggle: (row: Row, checked: boolean) => void;
    testIdPrefix?: string;
};

const buildDisabledReasonIcon = (testIdPrefix: string, { kind, message }: IncentiveDisabledReason, incentiveId: number) => (
    <Popover
        padding={true}
        data-testid={`${testIdPrefix}incentive-${kind}-popover-${incentiveId}`}
        position="left"
        trigger="hover"
        popoverContent={message}
    >
        <button
            type="button"
            data-testid={`${testIdPrefix}${kind}-info-${incentiveId}`}
            aria-label={message}
            style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                marginLeft: 4,
                cursor: 'help',
                display: 'inline-flex',
                alignItems: 'center',
                flexShrink: 0
            }}
        >
            <InformationCircleIcon />
        </button>
    </Popover>
);

export const IncentivesTableView = <Row extends IncentivesTableViewRow>({
    rows,
    getDisabledReason,
    isCheckboxDisabled,
    onToggle,
    testIdPrefix = ''
}: IncentivesTableViewProps<Row>) => {
    const isProgramIncentivesRestorationEnabled = useSelector(featureToggleSelectors.isProgramIncentivesRestorationEnabled);

    const columns = useMemo(
        () =>
            isProgramIncentivesRestorationEnabled
                ? nestedColumnsCompactUI
                : nestedColumnsCompactUI.filter((column) => column.dataIndex !== 'incentiveType'),
        [isProgramIncentivesRestorationEnabled]
    );

    const tableData = useMemo(
        () =>
            rows.map((row) => {
                const disabledReason = getDisabledReason(row);
                const checkboxId = `${testIdPrefix}checkbox-${row.incentiveId}`;
                const checkbox = (
                    <CheckBox
                        data-testid={checkboxId}
                        id={checkboxId}
                        name={checkboxId}
                        value={checkboxId}
                        checked={row.selected}
                        disabled={isCheckboxDisabled(row, disabledReason)}
                        onChange={(e) => {
                            const { checked } = e.target.value;
                            onToggle(row, checked);
                        }}
                    />
                );
                return {
                    ...row,
                    included: disabledReason ? (
                        <div style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                            {checkbox}
                            {buildDisabledReasonIcon(testIdPrefix, disabledReason, row.incentiveId)}
                        </div>
                    ) : (
                        checkbox
                    ),
                    'actions-icon':
                        row.description && row.description !== row.programName ? (
                            <Popover
                                padding={true}
                                data-testid={`${testIdPrefix}incentive-details-popover`}
                                position="left"
                                trigger="outsideClick"
                                popoverContent={row.description}
                            >
                                <Action data-testid={`${testIdPrefix}show-details-action`} size="sm">
                                    Show Details
                                </Action>
                            </Popover>
                        ) : null
                };
            }),
        [rows, getDisabledReason, isCheckboxDisabled, onToggle, testIdPrefix]
    );

    return (
        <Table
            data-testid={`${testIdPrefix}incentives-table`}
            className="incentives-table"
            scrollX={976}
            columns={columns}
            data={tableData}
            borderType="body"
            bordered={false}
        />
    );
};
